/**
 * Objective weights on a strategy (roadmap P-9; PRD-objective-weights-and-
 * strategic-coverage §3, slices A3/A4).
 *
 * Each objective's share of the strategy's 100 points. Changing one weight
 * moves the unlocked others in proportion so the total stays at 100
 * (`reallocate` in weightAllocation.ts); "Split evenly" and "Scale to 100"
 * reset the set. Saving writes the whole set at once through
 * set_objective_weights, which refuses anything that does not add up to 100.
 * Weight is a readout of where the strategy puts its investment — nothing
 * here feeds ranking (PRD §4, open question O4).
 */

import { useEffect, useMemo, useState } from 'react';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { setObjectiveWeights, type StrategicObjective } from '../../../lib/strategyAlignment';
import {
  evenSplit,
  isBalanced,
  normalizeTo100,
  reallocate,
  totalWeight,
  type WeightMap,
} from '../../../lib/weightAllocation';

function fromObjectives(objectives: StrategicObjective[]): WeightMap {
  return Object.fromEntries(objectives.map((o) => [o.id, Math.round(o.weight)]));
}

export function ObjectiveWeightsPanel({
  strategyId,
  objectives,
  onSaved,
}: {
  strategyId: string;
  objectives: StrategicObjective[];
  onSaved: () => void;
}) {
  const saved = useMemo(() => fromObjectives(objectives), [objectives]);
  const savedTotal = totalWeight(saved);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<WeightMap>(saved);
  const [locked, setLocked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(saved);
  }, [saved, editing]);

  if (objectives.length === 0) return null;

  const total = totalWeight(draft);
  const startEditing = () => {
    // An unweighted or unbalanced strategy opens on a set that adds up.
    setDraft(isBalanced(saved) ? saved : savedTotal > 0 ? normalizeTo100(saved) : evenSplit(objectives.map((o) => o.id)));
    setLocked(new Set());
    setNote(null);
    setError(null);
    setEditing(true);
  };

  const change = (id: string, value: number) => {
    const r = reallocate(draft, id, Number.isFinite(value) ? value : 0, locked);
    setDraft(r.weights);
    setNote(r.clampedReason);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const result = await setObjectiveWeights(strategyId, draft);
    setSaving(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setEditing(false);
    onSaved();
  };

  const header = (
    <div className="flex items-center justify-between mb-3">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">Objective weights</h3>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          Each objective&rsquo;s share of this strategy, out of 100.
        </p>
      </div>
      {!editing && (
        <button
          type="button"
          onClick={startEditing}
          className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)]"
        >
          {savedTotal === 0 ? 'Set weights' : 'Change weights'}
        </button>
      )}
    </div>
  );

  if (!editing) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5" data-testid="objective-weights">
        {header}
        {savedTotal === 0 ? (
          <p className="text-sm text-[var(--text-sec)]">Not weighted yet.</p>
        ) : (
          <>
            <ul className="space-y-1.5">
              {objectives.map((o) => (
                <li key={o.id} className="flex items-center gap-3 text-sm">
                  <span className="min-w-0 flex-1 truncate text-[var(--text-pri)]">{o.name}</span>
                  <span className="h-1.5 w-28 bg-[var(--hover-tint)]">
                    <span className="block h-full bg-[var(--slate)]" style={{ width: `${Math.min(100, o.weight)}%` }} />
                  </span>
                  <span className="w-10 text-right tabular-nums text-[var(--text-pri)]">{Math.round(o.weight)}</span>
                </li>
              ))}
            </ul>
            {savedTotal !== 100 && (
              <p className="mt-3 text-xs text-[var(--mustard)]">
                These add up to {Math.round(savedTotal)}, because an objective was added to or taken off this strategy
                after the weights were set. Change the weights to bring them back to 100.
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5" data-testid="objective-weights">
      {header}
      <ul className="space-y-2">
        {objectives.map((o) => {
          const isLocked = locked.has(o.id);
          return (
            <li key={o.id} className="flex items-center gap-3 text-sm">
              <span className="min-w-0 flex-1 truncate text-[var(--text-pri)]">{o.name}</span>
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={draft[o.id] ?? 0}
                disabled={isLocked}
                onChange={(e) => change(o.id, e.target.valueAsNumber)}
                aria-label={`Weight of ${o.name}`}
                className="w-20 border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1 text-right tabular-nums text-[var(--text-pri)] disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() =>
                  setLocked((prev) => {
                    const next = new Set(prev);
                    if (next.has(o.id)) next.delete(o.id);
                    else next.add(o.id);
                    return next;
                  })
                }
                aria-pressed={isLocked}
                aria-label={isLocked ? `Unlock ${o.name}` : `Lock ${o.name}`}
                title={isLocked ? 'Locked — other changes leave this one alone' : 'Lock this weight'}
                className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
              >
                {isLocked ? <LockClosedIcon className="h-4 w-4" /> : <LockOpenIcon className="h-4 w-4" />}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 flex items-center justify-between gap-3 text-sm">
        <span className={total === 100 ? 'text-[var(--text-sec)]' : 'text-[var(--coral)]'}>
          Total <span className="tabular-nums">{total}</span> of 100
        </span>
        <span className="flex gap-3 text-xs">
          <button
            type="button"
            className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            onClick={() => {
              setDraft(evenSplit(objectives.map((o) => o.id)));
              setNote(null);
            }}
          >
            Split evenly
          </button>
          {total !== 100 && (
            <button
              type="button"
              className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"
              onClick={() => {
                setDraft(normalizeTo100(draft));
                setNote(null);
              }}
            >
              Scale to 100
            </button>
          )}
        </span>
      </div>
      {note && <p className="mt-2 text-xs text-[var(--mustard)]">{note}</p>}
      {error && <p className="mt-2 text-xs text-[var(--coral)]">{error}</p>}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="px-3 py-1.5 text-sm text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || total !== 100}
          className="px-3 py-1.5 text-sm font-medium text-white bg-[var(--slate)] disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save weights'}
        </button>
      </div>
    </div>
  );
}
